import React, { useState } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import {
  PlayArrow as PlayIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Headphones as AudioIcon,
  Videocam as VideoIcon,
  BrokenImage as BrokenImageIcon,
} from '@mui/icons-material';

interface MediaMessageRendererProps {
  mediaUrl: string;
  mediaType: 'image' | 'document' | 'audio' | 'video' | 'sticker' | string;
  filename?: string;
  mimeType?: string;
  fileSize?: number;
  onMediaClick?: () => void;
  isOutgoing?: boolean;
}

// Format file size to human-readable format
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Get file extension from filename or mime type
const getFileExtension = (filename?: string, mimeType?: string): string => {
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext) return ext.toUpperCase();
  }
  if (mimeType) {
    const typeParts = mimeType.split('/');
    if (typeParts.length > 1) {
      const subtype = typeParts[1].split(';')[0];
      if (subtype === 'pdf') return 'PDF';
      if (subtype === 'msword' || subtype.includes('wordprocessing')) return 'DOC';
      if (subtype.includes('spreadsheet') || subtype === 'vnd.ms-excel') return 'XLS';
      if (subtype.includes('presentation') || subtype === 'vnd.ms-powerpoint') return 'PPT';
      return subtype.toUpperCase().slice(0, 4);
    }
  }
  return 'FILE';
};

// Get icon for file type
const getFileIcon = (mediaType: string, mimeType?: string) => {
  if (mediaType === 'image' || mimeType?.startsWith('image/')) {
    return <BrokenImageIcon sx={{ fontSize: 32, color: '#6B7280' }} />;
  }
  if (mimeType?.includes('pdf') || mediaType === 'document') {
    return <PdfIcon sx={{ fontSize: 32, color: '#EF4444' }} />;
  }
  if (mimeType?.includes('word') || mimeType?.includes('document')) {
    return <DocumentIcon sx={{ fontSize: 32, color: '#3B82F6' }} />;
  }
  if (mimeType?.includes('audio') || mediaType === 'audio') {
    return <AudioIcon sx={{ fontSize: 32, color: '#8B5CF6' }} />;
  }
  if (mimeType?.includes('video') || mediaType === 'video') {
    return <VideoIcon sx={{ fontSize: 32, color: '#F59E0B' }} />;
  }
  return <FileIcon sx={{ fontSize: 32, color: '#6B7280' }} />;
};

// Image thumbnail component
const ImageThumbnail: React.FC<{
  src: string;
  alt?: string;
  onClick?: () => void;
  isOutgoing?: boolean;
}> = ({ src, alt, onClick, isOutgoing }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: 240,
          minHeight: 120,
          bgcolor: isOutgoing ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.03)',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 2,
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 40, color: '#9CA3AF' }} />
        <Typography variant="caption" color="text.secondary">
          Image unavailable
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: 240,
        '&:active': {
          transform: 'scale(0.98)',
        },
        transition: 'transform 0.1s ease',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isOutgoing ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.03)',
            minHeight: 120,
          }}
        >
          <CircularProgress size={24} sx={{ color: '#9CA3AF' }} />
        </Box>
      )}
      <img
        src={src}
        alt={alt || 'Image'}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
        style={{
          display: loading ? 'none' : 'block',
          width: '100%',
          maxWidth: 240,
          height: 'auto',
          maxHeight: 280,
          objectFit: 'cover',
          borderRadius: 8,
        }}
      />
    </Box>
  );
};

// Audio player component
const AudioPlayer: React.FC<{
  src: string;
  filename?: string;
  isOutgoing?: boolean;
}> = ({ src, isOutgoing }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      audioRef.current.currentTime = percentage * duration;
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        width: '100%',
        maxWidth: 280,
        minWidth: 200,
      }}
    >
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
      
      <IconButton
        onClick={togglePlay}
        sx={{
          bgcolor: isOutgoing ? 'rgba(0,0,0,0.1)' : '#25D366',
          color: isOutgoing ? '#374151' : 'white',
          width: 40,
          height: 40,
          flexShrink: 0,
          '&:hover': {
            bgcolor: isOutgoing ? 'rgba(0,0,0,0.15)' : '#128C7E',
          },
        }}
      >
        {isPlaying ? (
          <Box sx={{ width: 16, height: 16, display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 4, height: 14, bgcolor: 'currentColor', borderRadius: 0.5 }} />
            <Box sx={{ width: 4, height: 14, bgcolor: 'currentColor', borderRadius: 0.5 }} />
          </Box>
        ) : (
          <PlayIcon />
        )}
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Progress bar */}
        <Box
          onClick={handleSeek}
          sx={{
            height: 6,
            bgcolor: isOutgoing ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.08)',
            borderRadius: 3,
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
              bgcolor: isOutgoing ? '#374151' : '#25D366',
              borderRadius: 3,
              transition: 'width 0.1s ease',
            }}
          />
        </Box>
        
        {/* Time display */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {formatTime(currentTime)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {duration > 0 ? formatTime(duration) : '--:--'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Video thumbnail component
const VideoThumbnail: React.FC<{
  src: string;
  filename?: string;
  onClick?: () => void;
  isOutgoing?: boolean;
}> = ({ src, onClick }) => {
  const [duration, setDuration] = useState<string>('');
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      if (dur && isFinite(dur)) {
        const mins = Math.floor(dur / 60);
        const secs = Math.floor(dur % 60);
        setDuration(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
    }
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: 240,
        bgcolor: '#000',
        '&:active': {
          transform: 'scale(0.98)',
        },
        transition: 'transform 0.1s ease',
      }}
    >
      <video
        ref={videoRef}
        src={src}
        onLoadedMetadata={handleLoadedMetadata}
        preload="metadata"
        style={{
          width: '100%',
          maxWidth: 240,
          height: 'auto',
          maxHeight: 200,
          display: 'block',
          objectFit: 'cover',
        }}
      />
      
      {/* Play button overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}
      >
        <PlayIcon sx={{ fontSize: 32, color: 'white', ml: 0.5 }} />
      </Box>
      
      {/* Duration badge */}
      {duration && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.7)',
            px: 1,
            py: 0.25,
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: 'white', fontSize: 11 }}>
            {duration}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Document card component
const DocumentCard: React.FC<{
  filename?: string;
  mimeType?: string;
  fileSize?: number;
  mediaType: string;
  onClick?: () => void;
  isOutgoing?: boolean;
}> = ({ filename, mimeType, fileSize, mediaType, onClick, isOutgoing }) => {
  const extension = getFileExtension(filename, mimeType);
  
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        bgcolor: isOutgoing ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.03)',
        borderRadius: 2,
        cursor: 'pointer',
        maxWidth: 280,
        minWidth: 200,
        '&:active': {
          bgcolor: isOutgoing ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.05)',
        },
        transition: 'background-color 0.1s ease',
      }}
    >
      {/* File icon with extension badge */}
      <Box
        sx={{
          position: 'relative',
          width: 48,
          height: 48,
          bgcolor: isOutgoing ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.05)',
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {getFileIcon(mediaType, mimeType)}
        <Box
          sx={{
            position: 'absolute',
            bottom: -4,
            right: -4,
            bgcolor: mimeType?.includes('pdf') ? '#EF4444' : 
                    mimeType?.includes('word') ? '#3B82F6' : 
                    mimeType?.includes('excel') || mimeType?.includes('spreadsheet') ? '#10B981' :
                    '#6B7280',
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
          }}
        >
          <Typography sx={{ color: 'white', fontSize: 8, fontWeight: 700 }}>
            {extension}
          </Typography>
        </Box>
      </Box>

      {/* File info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
            fontSize: 13,
          }}
        >
          {filename || 'Document'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            {formatFileSize(fileSize) || extension}
          </Typography>
        </Box>
      </Box>

      {/* Download indicator */}
      <DownloadIcon sx={{ fontSize: 20, color: '#9CA3AF', flexShrink: 0 }} />
    </Box>
  );
};

// Main component
export const MediaMessageRenderer: React.FC<MediaMessageRendererProps> = ({
  mediaUrl,
  mediaType,
  filename,
  mimeType,
  fileSize,
  onMediaClick,
  isOutgoing = false,
}) => {
  // Normalize media type
  const normalizedType = mediaType?.toLowerCase() || 'document';
  const isImage = normalizedType === 'image' || mimeType?.startsWith('image/');
  const isAudio = normalizedType === 'audio' || mimeType?.startsWith('audio/');
  const isVideo = normalizedType === 'video' || mimeType?.startsWith('video/');

  if (!mediaUrl) {
    return null;
  }

  // Render based on media type
  if (isImage) {
    return (
      <ImageThumbnail
        src={mediaUrl}
        alt={filename}
        onClick={onMediaClick}
        isOutgoing={isOutgoing}
      />
    );
  }

  if (isAudio) {
    return (
      <AudioPlayer
        src={mediaUrl}
        filename={filename}
        isOutgoing={isOutgoing}
      />
    );
  }

  if (isVideo) {
    return (
      <VideoThumbnail
        src={mediaUrl}
        filename={filename}
        onClick={onMediaClick}
        isOutgoing={isOutgoing}
      />
    );
  }

  // Default to document card
  return (
    <DocumentCard
      filename={filename}
      mimeType={mimeType}
      fileSize={fileSize}
      mediaType={normalizedType}
      onClick={onMediaClick}
      isOutgoing={isOutgoing}
    />
  );
};

export default MediaMessageRenderer;
